/**
 * Pre-sign warning modal — surfaces inline placeholder fields that the user
 * (or the AI) hasn't filled yet, before they commit to signing the document.
 *
 * Shown by App.tsx right before opening the signature-verification modal.
 * The user can:
 *   - "Fill them now" → modal closes, viewer focuses the first unfilled field
 *   - "Assign to recipient" → for future multi-party flow; today it's a stub
 *     that closes the modal and opens the configure flow upstream
 *   - "Sign as-is" → modal closes, signing proceeds with placeholders intact
 */
import { APP } from '../app/tokens';
import { PDF_VIEWER } from './pdfViewerTokens';

export interface UnfilledPlaceholderEntry {
  fieldId: string;
  label: string;
  page: number;
  placeholderRaw?: string;
}

interface UnfilledPlaceholderModalProps {
  open: boolean;
  unfilled: UnfilledPlaceholderEntry[];
  onFillNow: (firstFieldId: string) => void;
  onAssignToRecipient: () => void;
  onSignAnyway: () => void;
  onClose: () => void;
}

export default function UnfilledPlaceholderModal({
  open,
  unfilled,
  onFillNow,
  onAssignToRecipient,
  onSignAnyway,
  onClose,
}: UnfilledPlaceholderModalProps) {
  if (!open || unfilled.length === 0) return null;

  const first = unfilled[0];

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 460, maxWidth: 'calc(100vw - 48px)',
          backgroundColor: APP.bgContent, color: APP.textPrimary,
          borderRadius: 8, padding: 20,
          boxShadow: '0 12px 48px rgba(0,0,0,0.25)',
          fontFamily: 'inherit',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
          {unfilled.length === 1
            ? '1 placeholder is still unfilled'
            : `${unfilled.length} placeholders are still unfilled`}
        </div>
        <div style={{ fontSize: 13, color: APP.textSecondary, marginBottom: 14, lineHeight: 1.45 }}>
          The document has {unfilled.length === 1 ? 'a placeholder' : 'placeholders'} that
          {unfilled.length === 1 ? ' was' : ' were'} never replaced with a value. You can fill
          {unfilled.length === 1 ? ' it' : ' them'} now, hand off to a recipient, or sign as-is and
          leave {unfilled.length === 1 ? 'it' : 'them'} blank in the final PDF.
        </div>

        <div
          style={{
            maxHeight: 180, overflow: 'auto',
            border: `1px solid ${APP.border}`, borderRadius: 6,
            padding: 8, marginBottom: 16,
            backgroundColor: '#fafafa',
            fontSize: 12, lineHeight: 1.45,
          }}
        >
          {unfilled.slice(0, 8).map((entry) => (
            <div key={entry.fieldId} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '2px 0' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {entry.placeholderRaw ?? entry.label}
              </span>
              <span style={{ color: APP.textMuted, flexShrink: 0 }}>page {entry.page}</span>
            </div>
          ))}
          {unfilled.length > 8 && (
            <div style={{ color: APP.textMuted, paddingTop: 4 }}>
              +{unfilled.length - 8} more
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button
            onClick={onAssignToRecipient}
            style={{
              padding: '8px 14px', borderRadius: 6,
              border: `1px solid ${APP.border}`, cursor: 'pointer',
              backgroundColor: 'transparent', color: APP.textPrimary,
              fontSize: 12, fontWeight: 600,
            }}
          >
            Assign to recipient
          </button>
          <button
            onClick={onSignAnyway}
            style={{
              padding: '8px 14px', borderRadius: 6,
              border: `1px solid ${APP.border}`, cursor: 'pointer',
              backgroundColor: 'transparent', color: APP.textPrimary,
              fontSize: 12, fontWeight: 600,
            }}
          >
            Sign as-is
          </button>
          <button
            onClick={() => onFillNow(first.fieldId)}
            style={{
              padding: '8px 14px', borderRadius: 6,
              border: 'none', cursor: 'pointer',
              backgroundColor: PDF_VIEWER.fieldActive, color: '#fff',
              fontSize: 12, fontWeight: 700,
            }}
          >
            Fill them now
          </button>
        </div>
      </div>
    </div>
  );
}
